import React from 'react';
import {
    progressPercentFromChecklist,
    type ChecklistItem,
    type ChecklistStatus,
} from '../../features/process/progress/processMappingProgress';
import '../../styles/modern-theme.css';
import '../../styles/interview-redesign.css';
import { useTranslation } from 'react-i18next';
import { ScrollingNotification } from '../../components/notifications/ScrollingNotification';
import { useInterviewRailMessages } from '../../hooks/useInterviewRailNotifications';
import { ChevronDown, ChevronUp, GripVertical, Pencil, Trash2, UserRound, UserRoundPlus, ArrowLeftRight, Zap } from 'lucide-react';
import Dropdown from '../../components/Dropdown';
import { typeProcess } from '../../components/type';
import InterviewContextTopBar from './InterviewContextTopBar';
import SidebarUserProfileSection from '../../components/SidebarUserProfileSection';
import { useAppSelector } from '../../stores';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../services/api/uiButtonStatsService';
import {
    formatInviteRoleLabel,
    isProcessOwnerRoleLabel,
    normalizeInviteRoleKey,
} from '../../utils/inviteRoleOptions';

/** TEMP: person DnD off — restore by setting true */
const ENABLE_PERSON_DND = false;

/** Pastille de rôle : couleurs alternées par position dans la sidebar (indépendant des invités). */
const ROLE_DOT_PALETTE = [
    'var(--role-dot-1)',
    'var(--role-dot-2)',
    'var(--role-dot-3)',
    'var(--role-dot-4)',
    'var(--role-dot-5)',
    'var(--role-dot-6)',
];

interface Stakeholder {
    id: string;
    name: string;
    role: string;
    color: string;
    initials?: string;
    isPending?: boolean;
    /** Interview Mongo _id (absent si invitation en attente sans _id) */
    interviewMongoId?: string;
    forOwner?: boolean;
    backendRole?: string | null;
    invitationTokenId?: string;
    email?: string;
    /** Suppression autorisée (doublon même user + même rôle), comme InterviewList */
    isDeletable?: boolean;
    /** Identifiant utilisateur collaborateur (pour filtrer la ligne « vous ») */
    collaborationUserId?: string;
}

interface ModernInterviewLayoutProps {
    processName: string;
    stakeholders: Stakeholder[];
    diagrams: any[];
    insights: any[];
    children: React.ReactNode;
    progressPercent?: number;
    progressChecklist?: ChecklistItem[];
    joinedStakeholders?: number;
    completedInterviews?: number;
    totalStakeholders?: number;
    onBack: () => void;
    onAddStakeholder?: () => void;
    onGenerateDiagram?: () => void;
    onDiagramClick?: (diagram: any) => void;
    onDiagramHover?: (diagram: any) => void;
    onDiagramApprove?: (diagram: any) => void;
    onDiagramRename?: (diagram: any) => void;
    onDiagramShare?: (diagram: any) => void;
    onDiagramDelete?: (diagram: any) => void;
    onRunAnalysis?: () => void;
    onStartInterview?: () => void;
    onOpenLibrary?: () => void;
    onExportTranscript?: () => void;
    onShare?: () => void;
    processType: typeProcess;
    /** Remplacer / rappel collaborateur (même flux que InterviewList) */
    onCollaboratorMenuReplace?: (backendRole: string, replaceId: string, action: 'replace' | 'reminder') => void;
    /** Ouvre la confirmation de suppression d’interview */
    onCollaboratorMenuDelete?: (interviewMongoId: string) => void;
    /** Interview actuellement affiché (surlignage sidebar) */
    activeInterviewId?: string;
    /** PO / admin : ouvrir l’interview d’un contributeur */
    onSelectContributorInterview?: (interviewMongoId: string) => void;
    /** Inviter / remplacer / rappel : si défini, prioritaire sur le rôle session */
    canManageProcessCollaborators?: boolean;
    /** Afficher la ligne forçage / compte à rebours (owner, cooldown, ou carte en cours de génération) */
    showForceGenSidebar?: boolean;
    /** Swimlane pas encore finalisée — libellé « génération en cours » si pas de cooldown */
    mapGenerationPending?: boolean;
    /** Pending périmés (TTL 10 min) — génération bloquée / expirée */
    mapGenerationTimedOut?: boolean;
    /** Progression batch Macro / V1 / V2 pendant la génération */
    mappingGenProgress?: { ready: number; total: number; remaining?: number };
    /** Décompte 10 min basé sur created_at des mappings pending */
    mappingGenCountdown?: number | null;
    /** Chargement initial / refresh de la liste des cartographies */
    diagramsLoading?: boolean;
    /** ID carto en cours d'ouverture (navigation + crédits) */
    openingDiagramId?: string | null;
    /** Décompte 10 min après fin d'interview document (sidebar Diagrams) */
    documentMapBuildCountdown?: number | null;
    /** Statut API : cooldown, can_force_generate — aligné sur diagramList / ProcessInterview */
    forceGenStatus?: {
        isProcessOwner: boolean;
        /** Owner / co-owner / admin — droit de forçage carto */
        canForceGenerateMap?: boolean;
        allInterviewsCompleted: boolean;
        canForceGenerate: boolean;
        cooldownRemainingSeconds: number;
    };
    forceGenLoading?: boolean;
    extraHeaderActions?: React.ReactNode;
    /** Ordre d'affichage + rôles custom vides */
    roleOrder?: string[];
    /** Ouvre la modale de renommage de rôle */
    onRenameRole?: (from: string) => void;
    /** Ouvre la modale de création de rôle */
    onCreateRole?: () => void;
    onDeleteRole?: (label: string) => void;
    onReorderRoles?: (orderedLabels: string[]) => void;
    onMovePerson?: (memberId: string, toRole: string) => void;
    /** Ouvre la modale pour changer le rôle d'une personne */
    onChangePersonRole?: (memberId: string, currentRole: string, personName: string) => void;
    onInviteToRole?: (roleLabel: string) => void;
}

const ModernInterviewLayout: React.FC<ModernInterviewLayoutProps> = ({
    processName,
    stakeholders,
    diagrams,
    insights,
    children,
    progressPercent = 0,
    progressChecklist = [],
    joinedStakeholders = 0,
    completedInterviews = 0,
    totalStakeholders = 0,
    onBack,
    onAddStakeholder,
    onGenerateDiagram,
    onDiagramClick,
    onDiagramHover,
    onDiagramApprove,
    onDiagramRename,
    onDiagramShare,
    onDiagramDelete,
    onRunAnalysis,
    onStartInterview,
    onOpenLibrary,
    onExportTranscript,
    onShare,
    processType,
    onCollaboratorMenuReplace,
    onCollaboratorMenuDelete,
    activeInterviewId,
    onSelectContributorInterview,
    canManageProcessCollaborators,
    showForceGenSidebar = false,
    mapGenerationPending = false,
    mapGenerationTimedOut = false,
    mappingGenProgress,
    mappingGenCountdown = null,
    diagramsLoading = false,
    openingDiagramId = null,
    documentMapBuildCountdown = null,
    forceGenStatus,
    forceGenLoading = false,
    extraHeaderActions,
    roleOrder = [],
    onRenameRole,
    onCreateRole,
    onDeleteRole,
    onReorderRoles,
    onMovePerson,
    onChangePersonRole,
    onInviteToRole,
}) => {
    const { t } = useTranslation();
    const railNotificationMessages = useInterviewRailMessages();
    const users = useAppSelector((state) => state.auth);
    const role = useAppSelector((state) => state.role.role);

    const safeStakeholders = stakeholders ?? [];
    const contributorStakeholdersFallback = safeStakeholders.filter((m) => !m.forOwner);
    // Utiliser ?? pour que 0 reste 0 (|| traitait 0 comme falsy et réinjectait le porteur → « 1 sur 1 »).
    const actualJoinedStakeholders =
        joinedStakeholders ?? contributorStakeholdersFallback.filter((member) => !member.isPending).length;
    const actualTotalStakeholders = totalStakeholders ?? contributorStakeholdersFallback.length;
    const diagramsCount = diagrams.length;
    const [showAllDiagrams, setShowAllDiagrams] = React.useState(false);
    const [expandedContributorRoles, setExpandedContributorRoles] = React.useState<Set<string>>(() => new Set());
    const [dragRoleFrom, setDragRoleFrom] = React.useState<string | null>(null);
    const [dragPersonId, setDragPersonId] = React.useState<string | null>(null);
    const [dropTargetRole, setDropTargetRole] = React.useState<string | null>(null);
    const [justMovedPersonId, setJustMovedPersonId] = React.useState<string | null>(null);
    const justMovedTimerRef = React.useRef<number | null>(null);
    const didPersonDragRef = React.useRef(false);
    const hasMoreDiagrams = diagrams.length > 3;
    const visibleDiagrams = showAllDiagrams ? diagrams : diagrams.slice(0, 3);

    const forceGenerateSidebarButton = React.useMemo(() => {
        if (!onGenerateDiagram || !showForceGenSidebar || !forceGenStatus) return null;
        const cd = forceGenStatus.cooldownRemainingSeconds;
        const showTimer = cd > 0;
        const label = showTimer
            ? t('views.processInterview.forceGenerateCooldownTimer', {
                  minutes: Math.floor(cd / 60),
                  seconds: cd % 60,
              })
            : mapGenerationPending
              ? t('views.processInterview.forceGenerateSidebarMapPending')
              : forceGenLoading
                ? t('views.processInterview.forceGenerateRequestPending')
                : t('views.processInterview.forceGenerate') || t('words.generate');
        const dimmed = forceGenLoading;
        const cooldownActive = cd > 0;
        return (
            <button
                type="button"
                className="add-btn2 force-generate-sidebar-btn w-100 text-start"
                style={{
                    opacity: showTimer ? 0.98 : dimmed ? 0.82 : 1,
                }}
                disabled={forceGenLoading || cooldownActive}
                onClick={onGenerateDiagram}
            >
                {forceGenLoading ? (
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden />
                ) : !(processType === 'IMAGE' && showTimer) ? (
                    <Zap size={12} strokeWidth={2} aria-hidden className="force-generate-sidebar-icon" />
                ) : null}
                <span className={processType === 'IMAGE' && showTimer ? 'force-generate-sidebar-label' : 'ms-1 force-generate-sidebar-label'}>{label}</span>
            </button>
        );
    }, [onGenerateDiagram, showForceGenSidebar, forceGenStatus, mapGenerationPending, forceGenLoading, processType, t]);

    const canManageCollaborators =
        typeof canManageProcessCollaborators === 'boolean'
            ? canManageProcessCollaborators
            : typeof role === 'string' && role.toUpperCase() !== 'WORKER';

    const normalizeRoleLabel = (role?: string): string => {
        const trimmed = role?.trim();
        if (!trimmed) return '';
        if (trimmed.toUpperCase() === 'WORKER') {
            return t('views.processInterview.contributor') || 'Contributor';
        }
        if (trimmed.toUpperCase() === 'OWNER' || trimmed.toUpperCase() === 'ADMIN') {
            return t('features.swimlane.processOwner') || 'Process Owner';
        }
        return formatInviteRoleLabel(trimmed) || trimmed;
    };

    const stepClassForStatus = (status: ChecklistStatus) => {
        if (status === 'complete') return 'sd-done';
        if (status === 'current') return 'sd-active';
        return 'sd-pending';
    };

    const allInterviewsCompleted = actualTotalStakeholders > 0 && completedInterviews >= actualTotalStakeholders;
    const someInterviewsCompleted = completedInterviews > 0;

    const fallbackChecklist: ChecklistItem[] = [
        { id: 'owner.setScope', label: t('words.scopeSet'), status: 'complete' },
        { id: 'owner.inviteStakeholders', label: t('views.processInterview.stakeholdersInvited') || t('views.processInterview.collaborators'), status: actualJoinedStakeholders > 0 ? 'complete' : 'upcoming' },
        { 
            id: 'owner.interviewsInProgress', 
            label: t('views.processInterview.interviewsInProgress') || t('words.interviews'), 
            status: allInterviewsCompleted ? 'complete' : (someInterviewsCompleted ? 'current' : 'upcoming'), 
            meta: actualTotalStakeholders > 0 ? `${completedInterviews}/${actualTotalStakeholders}` : undefined 
        },
        { 
            id: 'owner.interviewsCompleted', 
            label: t('views.processInterview.interviewsDone') || t('words.interviews'), 
            status: allInterviewsCompleted ? 'complete' : 'upcoming', 
            meta: actualTotalStakeholders > 0 ? `${completedInterviews}/${actualTotalStakeholders}` : undefined 
        },
        { id: 'owner.discoveryCompleted', label: t('views.processInterview.mapsAvailable') || t('words.diagrams'), status: diagramsCount > 0 ? 'complete' : (mapGenerationPending || mapGenerationTimedOut ? 'current' : 'upcoming') }
    ];


    // Use real progressChecklist data when available
    const checklistItems = progressChecklist.length > 0 ? progressChecklist : fallbackChecklist;

    const displayProgressPercent = Math.max(
        progressPercent,
        progressPercentFromChecklist(checklistItems),
    );

    const checklistWithDetails = checklistItems.map((item) => {
        let detail: string | undefined = item.meta;
        if (item.id === 'owner.setScope') {
            detail = processName;
        } else if (item.id === 'owner.inviteStakeholders') {
            detail = `${actualJoinedStakeholders} ${t('words.of')} ${actualTotalStakeholders} ${t('views.processInterview.collaborators')}`;
        } else if (item.id === 'owner.interviewsCompleted') {
            detail = `${completedInterviews} ${t('words.of')} ${actualTotalStakeholders} ${t('words.completed')}`;
        } else if (item.id === 'owner.discoveryCompleted') {
            const forceCooldownSec = forceGenStatus?.cooldownRemainingSeconds ?? 0;
            if (mapGenerationPending && mappingGenProgress && mappingGenProgress.total > 0) {
                const progressLabel = t('views.processInterview.mapsGeneratingProgress', {
                    remaining: mappingGenProgress.remaining ?? Math.max(0, mappingGenProgress.total - mappingGenProgress.ready),
                    total: mappingGenProgress.total,
                    ready: mappingGenProgress.ready,
                });
                detail =
                    mappingGenCountdown != null && mappingGenCountdown > 0
                        ? `${progressLabel} · ${t('features.swimlane.mapGenCountdown', {
                              mm: String(Math.floor(mappingGenCountdown / 60)).padStart(2, '0'),
                              ss: String(mappingGenCountdown % 60).padStart(2, '0'),
                          })}`
                        : progressLabel;
            } else if (mapGenerationPending) {
                detail =
                    mappingGenCountdown != null && mappingGenCountdown > 0
                        ? `${t('views.processInterview.forceGenerateSidebarMapPending')} · ${t('features.swimlane.mapGenCountdown', {
                              mm: String(Math.floor(mappingGenCountdown / 60)).padStart(2, '0'),
                              ss: String(mappingGenCountdown % 60).padStart(2, '0'),
                          })}`
                        : t('views.processInterview.forceGenerateSidebarMapPending');
            } else if (forceCooldownSec > 0) {
                // Même décompte que le bouton ⚡ sous DIAGRAMMES (+ nombre de cartos si déjà prêtes)
                const cooldownLabel = t('views.processInterview.forceGenerateCooldownTimer', {
                    minutes: Math.floor(forceCooldownSec / 60),
                    seconds: forceCooldownSec % 60,
                });
                detail =
                    diagramsCount > 0
                        ? `${diagramsCount} ${t('views.processInterview.diagramsGenerated') || t('words.generated')} · ${cooldownLabel}`
                        : cooldownLabel;
            } else if (mapGenerationTimedOut && diagramsCount === 0) {
                detail = t('views.processInterview.mapsGenerationTimedOut');
            } else {
                detail = `${diagramsCount} ${t('views.processInterview.diagramsGenerated') || t('words.generated')}`;
            }
        }
        return { ...item, detail };
    });

    const getInitials = (name: string) => {
        // Use initials from stakeholder data if available, otherwise calculate
        const stakeholder = stakeholders.find(s => s.name === name);
        if (stakeholder?.initials) {
            return stakeholder.initials;
        }
        
        const trimmed = name?.trim() || '';
        if (!trimmed) return '--';
        const parts = trimmed.split(' ').filter(Boolean);
        const initials = parts.slice(0, 2).map(part => part[0]?.toUpperCase()).join('');
        return initials || trimmed.substring(0, 2).toUpperCase();
    };

    const toRgba = (hex: string, alpha: number) => {
        const sanitized = (hex || '').replace('#', '');
        if (sanitized.length !== 3 && sanitized.length !== 6) {
            return `rgba(255,255,255,0.06)`;
        }
        const full = sanitized.length === 3
            ? sanitized.split('').map((c) => `${c}${c}`).join('')
            : sanitized;
        const value = Number.parseInt(full, 16);
        if (Number.isNaN(value)) {
            return `rgba(255,255,255,0.06)`;
        }
        const r = (value >> 16) & 255;
        const g = (value >> 8) & 255;
        const b = value & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Grouper les stakeholders par rôle métier (clé normalisée : pas de doublon Client / l'entrepôt / Entrepôt), respecter role_order.
    // Process Owner group always stays first.
    const processOwnerDisplayLabel = (
        t('features.swimlane.processOwner') || 'Process Owner'
    ).trim();

    const isOwnerRoleGroup = React.useCallback(
        (role: string, members: Stakeholder[]) => {
            if (members.some((m) => m.forOwner)) return true;
            return isProcessOwnerRoleLabel(role, processOwnerDisplayLabel);
        },
        [processOwnerDisplayLabel]
    );

    const roleEntries = React.useMemo(() => {
        const grouped = new Map<string, { label: string; members: Stakeholder[] }>();
        const contributorFallback = t('views.processInterview.contributor') || 'Collaborator';
        const processOwnerFallback = (t('features.swimlane.processOwner') || 'Process Owner').trim();

        const roleKeyFor = (raw?: string | null, fallbackLabel?: string): string =>
            normalizeInviteRoleKey(raw || '') ||
            normalizeInviteRoleKey(fallbackLabel || '') ||
            'contributor';

        safeStakeholders.forEach((sh) => {
            if (sh.forOwner) {
                const existing = grouped.get('process-owner');
                if (existing) {
                    existing.members.push(sh);
                } else {
                    grouped.set('process-owner', { label: processOwnerFallback, members: [sh] });
                }
                return;
            }
            const raw = (sh.backendRole || sh.role || '').trim();
            const key = roleKeyFor(raw, sh.role);
            const label =
                normalizeRoleLabel(raw) ||
                normalizeRoleLabel(sh.role) ||
                contributorFallback;
            const existing = grouped.get(key);
            if (existing) {
                existing.members.push(sh);
                if (label && label.length < existing.label.length) {
                    existing.label = label;
                }
            } else {
                grouped.set(key, { label, members: [sh] });
            }
        });

        // Rôles connus du processus sans personne assignée : groupe vide visible
        (roleOrder || []).forEach((raw) => {
            const label = normalizeRoleLabel(raw) || raw.trim();
            if (!label) return;
            const key = isProcessOwnerRoleLabel(label, processOwnerFallback)
                ? 'process-owner'
                : roleKeyFor(raw, label);
            if (grouped.has(key)) return;
            grouped.set(key, { label, members: [] });
        });

        const entries = Array.from(grouped.entries()).map(([mapKey, { label, members }]) => ({
            role: label,
            roleKey: mapKey,
            members,
        }));
        const orderKeys = (roleOrder || []).map((r) =>
            isProcessOwnerRoleLabel(normalizeRoleLabel(r) || r, processOwnerFallback)
                ? 'process-owner'
                : roleKeyFor(r, normalizeRoleLabel(r) || r)
        );

        entries.sort((a, b) => {
            const aOwner = isOwnerRoleGroup(a.role, a.members);
            const bOwner = isOwnerRoleGroup(b.role, b.members);
            if (aOwner && !bOwner) return -1;
            if (!aOwner && bOwner) return 1;

            if (!orderKeys.length) return 0;
            const ai = orderKeys.indexOf(a.roleKey);
            const bi = orderKeys.indexOf(b.roleKey);
            if (ai === -1 && bi === -1) return 0;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
        });
        return entries;
    }, [safeStakeholders, roleOrder, t, isOwnerRoleGroup]);

    const resolveDeleteId = (member: Stakeholder): string | null => {
        if (member.interviewMongoId) return member.interviewMongoId;
        if (member.invitationTokenId) return `pending-${member.invitationTokenId}`;
        return null;
    };

    const resolveReplaceId = (member: Stakeholder): string | null => {
        if (member.isPending) {
            const token = member.invitationTokenId || member.email || '';
            return token ? `pending-${token}` : null;
        }
        return member.interviewMongoId || null;
    };

    const currentUserId = users.user?._id != null ? String(users.user._id) : '';
    const currentUserEmail = (users.user?.email || '').trim().toLowerCase();

    const isCurrentStakeholder = React.useCallback(
        (m: Stakeholder) => {
            if (currentUserId && m.collaborationUserId && String(m.collaborationUserId) === currentUserId) {
                return true;
            }
            const em = (m.email || '').trim().toLowerCase();
            if (em && currentUserEmail && em === currentUserEmail) return true;
            return false;
        },
        [currentUserEmail, currentUserId],
    );

    const toggleContributorRole = (roleKey: string) => {
        void trackUiButtonEventAsync(
            UI_BUTTON_EVENTS.interview.modernToggleRoleGroup
        );
        setExpandedContributorRoles((prev) => {
            const next = new Set(prev);
            if (next.has(roleKey)) next.delete(roleKey);
            else next.add(roleKey);
            return next;
        });
    };

    const clearPersonDragState = React.useCallback(() => {
        setDragPersonId(null);
        setDropTargetRole(null);
    }, []);

    const markPersonJustMoved = React.useCallback((memberId: string) => {
        if (justMovedTimerRef.current != null) {
            window.clearTimeout(justMovedTimerRef.current);
        }
        setJustMovedPersonId(memberId);
        justMovedTimerRef.current = window.setTimeout(() => {
            setJustMovedPersonId(null);
            justMovedTimerRef.current = null;
        }, 220);
    }, []);

    React.useEffect(() => {
        return () => {
            if (justMovedTimerRef.current != null) {
                window.clearTimeout(justMovedTimerRef.current);
            }
        };
    }, []);

    // Safety net: a drop outside any role block never triggers our handlers,
    // which would leave the sidebar stuck in `role-dnd-active`.
    React.useEffect(() => {
        if (!dragPersonId) return;
        const reset = () => clearPersonDragState();
        document.addEventListener('dragend', reset);
        document.addEventListener('drop', reset);
        return () => {
            document.removeEventListener('dragend', reset);
            document.removeEventListener('drop', reset);
        };
    }, [dragPersonId, clearPersonDragState]);

    const startPersonDrag = React.useCallback((e: React.DragEvent, memberId: string) => {
        didPersonDragRef.current = true;
        setDragRoleFrom(null);
        setDragPersonId(memberId);
        setDropTargetRole(null);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', memberId);
    }, []);

    const endPersonDrag = React.useCallback(() => {
        clearPersonDragState();
        // Swallow the synthetic click that can follow dragend
        window.setTimeout(() => {
            didPersonDragRef.current = false;
        }, 120);
    }, [clearPersonDragState]);
    const getMemberCurrentRole = React.useCallback(
        (memberId: string): string | null => {
            for (const entry of roleEntries) {
                if (entry.members.some((m) => m.id === memberId)) {
                    return entry.role;
                }
            }
            return null;
        },
        [roleEntries]
    );

    return (
        <div className="interview-shell-container flex-grow-1">
            <div
                className={[
                    'interview-shell',
                    dragPersonId ? 'role-dnd-active' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <div className="shell">
                    {/* LEFT PANEL */}
                    <div className="left-panel">
                        <div className="lp-header">
                            <div className="back-btn" onClick={onBack}>
                                <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                    <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {t('words.home')}
                            </div>
                        </div>

                        <div className="lp-section-scrollable">
                        {/* STAKEHOLDERS */}
                        <div className="lp-section">
                            <div className="lp-label">{t('views.processInterview.collaborators')}</div>
                            {roleEntries.map(({ role, roleKey, members }, roleIndex) => {
                                const roleColor = ROLE_DOT_PALETTE[roleIndex % ROLE_DOT_PALETTE.length];
                                const isExpanded = expandedContributorRoles.has(roleKey) || members.length === 0 || Boolean(dragPersonId);
                                // Compteur = personnes réellement invitées (pas le placeholder « Non invité »)
                                const invitedMembers = members.filter((m) => !String(m.id || '').startsWith('mentioned-'));
                                const invitedCount = invitedMembers.length;
                                const visibleMembers = isExpanded
                                    ? members
                                    : members.filter((m) => isCurrentStakeholder(m));
                                const needsToggle =
                                    members.length > 1 ||
                                    members.some((m) => !isCurrentStakeholder(m));
                                const isOwnerOnlyRole = isOwnerRoleGroup(roleKey, members);
                                const canManageRole =
                                    canManageCollaborators &&
                                    Boolean(onRenameRole || onDeleteRole || onReorderRoles) &&
                                    !isOwnerOnlyRole &&
                                    !dragPersonId;
                                const isPersonDroppable =
                                    Boolean(dragPersonId) &&
                                    !isOwnerOnlyRole &&
                                    getMemberCurrentRole(dragPersonId!) !== roleKey;
                                const isPersonDropTarget =
                                    isPersonDroppable && dropTargetRole === roleKey;

                                return (
                                    <div
                                        className={[
                                            'role-block',
                                            isPersonDroppable ? 'role-block--droppable' : '',
                                            isPersonDropTarget ? 'role-block--drop-target' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                        key={roleKey}
                                        onDragEnter={(e) => {
                                            if (!canManageCollaborators || !dragPersonId) return;
                                            if (isOwnerOnlyRole) {
                                                setDropTargetRole(null);
                                                return;
                                            }
                                            e.preventDefault();
                                            if (dropTargetRole !== roleKey) {
                                                setDropTargetRole(roleKey);
                                            }
                                        }}
                                        onDragOver={(e) => {
                                            if (!canManageCollaborators) return;
                                            if (dragPersonId) {
                                                if (isOwnerOnlyRole) return;
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                                if (dropTargetRole !== roleKey) {
                                                    setDropTargetRole(roleKey);
                                                }
                                                return;
                                            }
                                            if (dragRoleFrom) e.preventDefault();
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (!canManageCollaborators) return;
                                            if (dragPersonId && onMovePerson) {
                                                if (isOwnerOnlyRole) {
                                                    clearPersonDragState();
                                                    return;
                                                }
                                                const fromRole = getMemberCurrentRole(dragPersonId);
                                                if (fromRole !== roleKey) {
                                                    onMovePerson(dragPersonId, roleKey);
                                                    markPersonJustMoved(dragPersonId);
                                                }
                                                clearPersonDragState();
                                                return;
                                            }
                                            if (dragPersonId) return;
                                            if (dragRoleFrom && onReorderRoles && dragRoleFrom !== roleKey) {
                                                // Never let a métier role take Process Owner's first slot
                                                if (isOwnerOnlyRole) {
                                                    setDragRoleFrom(null);
                                                    return;
                                                }
                                                const reorderable = roleEntries.filter(
                                                    (entry) => !isOwnerRoleGroup(entry.role, entry.members)
                                                );
                                                const keys = reorderable.map((r) => r.roleKey);
                                                const fromIdx = keys.indexOf(dragRoleFrom);
                                                const toIdx = keys.indexOf(roleKey);
                                                if (fromIdx >= 0 && toIdx >= 0) {
                                                    const next = [...reorderable];
                                                    const [moved] = next.splice(fromIdx, 1);
                                                    next.splice(toIdx, 0, moved);
                                                    onReorderRoles(next.map((r) => r.role));
                                                }
                                                setDragRoleFrom(null);
                                            }
                                        }}
                                    >
                                        <div
                                            className={`role-header${needsToggle ? ' role-header--toggle' : ''}${canManageRole ? ' role-header--manageable' : ''}`}
                                            role={needsToggle ? 'button' : undefined}
                                            tabIndex={needsToggle ? 0 : undefined}
                                            onClick={
                                                needsToggle
                                                    ? () => toggleContributorRole(roleKey)
                                                    : undefined
                                            }
                                            onKeyDown={
                                                needsToggle
                                                    ? (e) => {
                                                          if (e.key === 'Enter' || e.key === ' ') {
                                                              e.preventDefault();
                                                              toggleContributorRole(roleKey);
                                                          }
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {canManageRole && (
                                                <span
                                                    className="role-grip"
                                                    draggable={!dragPersonId}
                                                    title={t('views.processInterview.dragRole', 'Drag to reorder')}
                                                    onDragStart={(e) => {
                                                        e.stopPropagation();
                                                        if (dragPersonId) {
                                                            e.preventDefault();
                                                            return;
                                                        }
                                                        setDragRoleFrom(roleKey);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                    }}
                                                    onDragEnd={() => setDragRoleFrom(null)}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <GripVertical size={12} />
                                                </span>
                                            )}
                                            <div className="role-dot" style={{ background: roleColor }} />
                                            <span className="role-name">{role}</span>
                                            <div className="role-header-end">
                                                {canManageRole && (
                                                    <div
                                                        className="role-header-actions"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {onRenameRole && (
                                                            <button
                                                                type="button"
                                                                className="role-action-btn"
                                                                title={t('words.rename')}
                                                                onClick={() => onRenameRole(role)}
                                                            >
                                                                <Pencil size={12} />
                                                            </button>
                                                        )}
                                                        {onInviteToRole && (
                                                            <button
                                                                type="button"
                                                                className="role-action-btn"
                                                                title={t('views.processInterview.addContributor', 'Add contributor')}
                                                                onClick={() => onInviteToRole(role)}
                                                            >
                                                                <UserRoundPlus size={12} />
                                                            </button>
                                                        )}
                                                        {onDeleteRole && (
                                                            <button
                                                                type="button"
                                                                className="role-action-btn role-action-btn--danger"
                                                                title={t('words.delete')}
                                                                onClick={() => onDeleteRole(role)}
                                                            >
                                                                <Trash2 size={12} />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                <span
                                                    className={
                                                        needsToggle
                                                            ? 'role-header-chevron'
                                                            : 'role-header-chevron role-header-chevron--reserved'
                                                    }
                                                    aria-hidden
                                                >
                                                    {needsToggle ? (
                                                        isExpanded ? (
                                                            <ChevronUp size={14} strokeWidth={2} />
                                                        ) : (
                                                            <ChevronDown size={14} strokeWidth={2} />
                                                        )
                                                    ) : null}
                                                </span>
                                                <span className="role-count">{invitedCount}</span>
                                            </div>
                                        </div>
                                        <div className="role-members">
                                            {isPersonDropTarget ? (
                                                <div className="role-drop-hint" aria-hidden>
                                                    {t('views.processInterview.dropHere', 'Drop here')}
                                                </div>
                                            ) : null}
                                            {members.length === 0 && !isPersonDropTarget && (
                                                <div className="role-empty-hint">
                                                    <span className="role-empty-avatar" aria-hidden>
                                                        <UserRound size={12} strokeWidth={1.8} />
                                                    </span>
                                                    <span>{t('views.processInterview.notInvited', 'Not invited')}</span>
                                                </div>
                                            )}
                                            {visibleMembers.map((member) => {
                                                const isMentionedPlaceholder = member.id.startsWith('mentioned-');
                                                const showPersonActions =
                                                    canManageCollaborators &&
                                                    !member.forOwner &&
                                                    !isMentionedPlaceholder &&
                                                    Boolean(onCollaboratorMenuReplace || onCollaboratorMenuDelete || onMovePerson);

                                                const canOpenInterview =
                                                    Boolean(onSelectContributorInterview) &&
                                                    Boolean(member.interviewMongoId) &&
                                                    !isMentionedPlaceholder &&
                                                    (canManageCollaborators || isCurrentStakeholder(member));
                                                const isViewerRow =
                                                    Boolean(activeInterviewId) &&
                                                    Boolean(member.interviewMongoId) &&
                                                    String(activeInterviewId) === String(member.interviewMongoId);
                                                const canDragPerson =
                                                    ENABLE_PERSON_DND &&
                                                    showPersonActions &&
                                                    Boolean(onMovePerson);

                                                return (
                                                <div
                                                    className={[
                                                        'role-person',
                                                        canOpenInterview ? 'role-person--clickable' : '',
                                                        isViewerRow ? 'role-person--active' : '',
                                                        showPersonActions ? 'role-person--manageable' : '',
                                                        canDragPerson ? 'role-person--draggable' : '',
                                                        dragPersonId === member.id ? 'role-person--dragging' : '',
                                                        justMovedPersonId === member.id ? 'role-person--just-moved' : '',
                                                    ]
                                                        .filter(Boolean)
                                                        .join(' ')}
                                                    key={member.id}
                                                    role={canOpenInterview ? 'button' : undefined}
                                                    tabIndex={canOpenInterview ? 0 : undefined}
                                                    draggable={canDragPerson}
                                                    onDragStart={
                                                        canDragPerson
                                                            ? (e) => {
                                                                  const target = e.target as HTMLElement;
                                                                  if (target.closest('.role-person-actions')) {
                                                                      e.preventDefault();
                                                                      return;
                                                                  }
                                                                  startPersonDrag(e, member.id);
                                                              }
                                                            : undefined
                                                    }
                                                    onDragEnd={canDragPerson ? endPersonDrag : undefined}
                                                    onClick={
                                                        canOpenInterview
                                                            ? () => {
                                                                  if (didPersonDragRef.current) {
                                                                      didPersonDragRef.current = false;
                                                                      return;
                                                                  }
                                                                  onSelectContributorInterview?.(
                                                                      member.interviewMongoId as string
                                                                  );
                                                              }
                                                            : undefined
                                                    }
                                                    onKeyDown={
                                                        canOpenInterview
                                                            ? (e) => {
                                                                  if (e.key === 'Enter' || e.key === ' ') {
                                                                      e.preventDefault();
                                                                      onSelectContributorInterview?.(
                                                                          member.interviewMongoId as string
                                                                      );
                                                                  }
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <div className="role-person-left">
                                                        {canDragPerson && (
                                                            <span
                                                                className="role-person-grip"
                                                                title={t(
                                                                    'views.processInterview.dragPerson',
                                                                    'Drag to another role'
                                                                )}
                                                                aria-hidden
                                                            >
                                                                <GripVertical size={14} />
                                                            </span>
                                                        )}
                                                        <div
                                                            className="mbadge"
                                                            style={{
                                                                background: toRgba(member.color || roleColor, 0.18),
                                                                color: member.color || roleColor
                                                            }}
                                                        >
                                                            {getInitials(member.name)}
                                                        </div>
                                                        <span className="role-person-name">{member.name}</span>
                                                    </div>
                                                    {showPersonActions && (
                                                        <div
                                                            className="role-person-actions"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {onChangePersonRole && (
                                                                <button
                                                                    type="button"
                                                                    className="role-action-btn"
                                                                    title={t('views.processInterview.changeRole', 'Change role')}
                                                                    onClick={() =>
                                                                        onChangePersonRole(
                                                                            member.id,
                                                                            member.backendRole || member.role,
                                                                            member.name
                                                                        )
                                                                    }
                                                                >
                                                                    <Pencil size={12} />
                                                                </button>
                                                            )}
                                                            {onCollaboratorMenuReplace && (
                                                                <button
                                                                    type="button"
                                                                    className="role-action-btn"
                                                                    title={t('words.replace')}
                                                                    onClick={() => {
                                                                        const br = member.backendRole || '';
                                                                        const rid = resolveReplaceId(member);
                                                                        if (rid) {
                                                                            onCollaboratorMenuReplace(br, rid, 'replace');
                                                                        }
                                                                    }}
                                                                >
                                                                    <ArrowLeftRight size={12} />
                                                                </button>
                                                            )}
                                                            {onCollaboratorMenuDelete && member.isDeletable !== false && (
                                                                <button
                                                                    type="button"
                                                                    className="role-action-btn role-action-btn--danger"
                                                                    title={t('words.delete')}
                                                                    onClick={() => {
                                                                        const delId = resolveDeleteId(member);
                                                                        if (delId) onCollaboratorMenuDelete(delId);
                                                                    }}
                                                                >
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            )}
                                                            <div className="role-person-menu role-person-menu--ellipsis">
                                                                <Dropdown
                                                                    onMenuButtonClick={() => {
                                                                        void trackUiButtonEventAsync(
                                                                            UI_BUTTON_EVENTS.interview
                                                                                .modernCollaboratorMenu
                                                                        );
                                                                    }}
                                                                    dropName=""
                                                                    dropdownType="btn-group dropend"
                                                                    iconClassName="fa fa-ellipsis-vertical small dropdown_icon"
                                                                    buttonDropClassName="bg-transparent border-0 role-dropdown-btn"
                                                                    styleButton={{}}
                                                                    action7={
                                                                        onChangePersonRole
                                                                            ? t(
                                                                                  'views.processInterview.changeRole',
                                                                                  'Change role'
                                                                              )
                                                                            : undefined
                                                                    }
                                                                    action7OnClick={
                                                                        onChangePersonRole
                                                                            ? () =>
                                                                                  onChangePersonRole(
                                                                                      member.id,
                                                                                      member.backendRole ||
                                                                                          member.role,
                                                                                      member.name
                                                                                  )
                                                                            : undefined
                                                                    }
                                                                    action4={t('words.replace')}
                                                                    onclickAction4={() => {
                                                                        const br = member.backendRole || '';
                                                                        const rid = resolveReplaceId(member);
                                                                        if (rid) {
                                                                            onCollaboratorMenuReplace?.(br, rid, 'replace');
                                                                        }
                                                                    }}
                                                                    action5={t('navigation.sendReminder')}
                                                                    onclickAction5={() => {
                                                                        const br = member.backendRole || '';
                                                                        if (member.isPending && member.invitationTokenId) {
                                                                            onCollaboratorMenuReplace?.(
                                                                                br,
                                                                                member.invitationTokenId ||
                                                                                    member.email ||
                                                                                    '',
                                                                                'reminder'
                                                                            );
                                                                        } else if (member.interviewMongoId) {
                                                                            onCollaboratorMenuReplace?.(
                                                                                br,
                                                                                member.interviewMongoId,
                                                                                'reminder'
                                                                            );
                                                                        }
                                                                    }}
                                                                    theRouteOfAction4=""
                                                                    theRouteOfAction5=""
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                            {canManageCollaborators && onCreateRole && (
                                <div
                                    className="add-btn2"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onCreateRole()}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onCreateRole();
                                        }
                                    }}
                                >
                                    <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    {t('views.processInterview.createRole', 'Add role')}
                                </div>
                            )}
                            {onAddStakeholder && (
                                <div className="add-btn2" onClick={onAddStakeholder}>
                                    <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    {t('views.processInterview.addContributor') || '+ Add Contributor'}
                                </div>
                            )}
                        </div>

                        <hr className="ndiv" />

                        {/* DIAGRAMS */}
                        <div className="lp-section">
                            <div className="lp-label">{t('words.diagrams')}</div>
                            {documentMapBuildCountdown !== null && documentMapBuildCountdown > 0 && (
                                <div
                                    className="dia-row diagram-map-build-countdown"
                                    style={{ cursor: 'default', pointerEvents: 'none', opacity: 0.98 }}
                                    role="status"
                                    aria-live="polite"
                                >
                                    <span style={{ fontSize: '0.8rem', flex: 1 }}>
                                        {t('views.processInterview.forceGenerateCooldownTimer', {
                                            minutes: Math.floor(documentMapBuildCountdown / 60),
                                            seconds: documentMapBuildCountdown % 60,
                                        })}
                                    </span>
                                </div>
                            )}
                            {diagramsLoading && diagrams.length === 0 && !documentMapBuildCountdown && !mapGenerationPending && (
                                <div className="diagram-section-loader" role="status" aria-live="polite">
                                    <span className="diagram-section-loader__spinner" aria-hidden />
                                    <span>{t('views.processInterview.diagramsLoading')}</span>
                                </div>
                            )}
                            {mapGenerationPending && !documentMapBuildCountdown && !diagramsLoading && (
                                <div className="diagram-section-loader diagram-section-loader--generating" role="status" aria-live="polite">
                                    <span className="diagram-section-loader__spinner" aria-hidden />
                                    <span className="diagram-section-loader__copy">
                                        <span>
                                            {mappingGenProgress && mappingGenProgress.total > 0
                                                ? t('views.processInterview.mapsGeneratingProgress', {
                                                    remaining: mappingGenProgress.remaining ?? Math.max(0, mappingGenProgress.total - mappingGenProgress.ready),
                                                    total: mappingGenProgress.total,
                                                    ready: mappingGenProgress.ready,
                                                })
                                                : t('views.processInterview.forceGenerateSidebarMapPending')}
                                        </span>
                                        {mappingGenCountdown != null && mappingGenCountdown > 0 && (
                                            <span className="diagram-section-loader__timer">
                                                {t('features.swimlane.mapGenCountdown', {
                                                    mm: String(Math.floor(mappingGenCountdown / 60)).padStart(2, '0'),
                                                    ss: String(mappingGenCountdown % 60).padStart(2, '0'),
                                                })}
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                            {mapGenerationTimedOut && !mapGenerationPending && diagramsCount === 0 && !documentMapBuildCountdown && !diagramsLoading && (
                                <div className="diagram-section-loader diagram-section-loader--timed-out" role="status" aria-live="polite">
                                    <span className="diagram-section-loader__copy">
                                        {t('views.processInterview.mapsGenerationTimedOut')}
                                    </span>
                                </div>
                            )}
                            <div className="diagram-list">
                                {visibleDiagrams.map((diagram, idx) => {
                                    const diagramLabel = diagram.name || `${t('words.diagram')} ${idx + 1}`;
                                    const diagramId = String(diagram.id || diagram._id || idx);
                                    const isOpening = openingDiagramId === diagramId;
                                    const canShowMenu = Boolean(onDiagramApprove || onDiagramRename || onDiagramShare || onDiagramDelete);

                                    return (
                                        <div
                                            key={diagramId}
                                            className={`dia-row ${isOpening ? 'dia-row--opening' : ''}`}
                                        >
                                            <div
                                                className={`dia-row-main ${isOpening ? 'dia-row-main--opening' : ''}`}
                                                onClick={() => {
                                                    if (!isOpening) onDiagramClick?.(diagram);
                                                }}
                                                onMouseEnter={() => onDiagramHover?.(diagram)}
                                                role="button"
                                                tabIndex={0}
                                                aria-busy={isOpening}
                                                aria-label={diagramLabel}
                                                onKeyDown={(event) => {
                                                    if (isOpening) return;
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        onDiagramClick?.(diagram);
                                                    }
                                                }}
                                            >
                                                <svg className="dia-row-icon" viewBox="0 0 16 16" fill="none" aria-hidden>
                                                    <path d="M2 5H8M2 8H12M2 11H10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                                </svg>
                                                <span className="dia-row-label">{diagramLabel}</span>
                                                <span className="dia-row-open-hint" aria-hidden>
                                                    {isOpening ? (
                                                        <span className="diagram-section-loader__spinner diagram-section-loader__spinner--inline" />
                                                    ) : (
                                                        <svg viewBox="0 0 16 16" fill="none">
                                                            <path
                                                                d="M6 3H13V10M13 3L3 13"
                                                                stroke="currentColor"
                                                                strokeWidth="1.2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </span>
                                            </div>
                                            {canShowMenu && (
                                                <div
                                                    className="diagram-actions"
                                                    onClick={(event) => event.stopPropagation()}
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                >
                                                    <Dropdown
                                                        onMenuButtonClick={() => {
                                                            void trackUiButtonEventAsync(
                                                                UI_BUTTON_EVENTS.interview
                                                                    .modernDiagramListMenu
                                                            );
                                                        }}
                                                        dropName=""
                                                        dropdownType="btn-group dropend"
                                                        iconClassName="fa fa-ellipsis-vertical small dropdown_icon"
                                                        buttonDropClassName="bg-transparent border-0 ms-2 small"
                                                        styleButton={{}}
                                                        aproval={t('words.requestApproval')}
                                                        rename={t('words.rename')}
                                                        share={t('words.share')}
                                                        action1={t('words.delete')}
                                                        deleteProcess={
                                                            onDiagramDelete
                                                                ? () => onDiagramDelete(diagram)
                                                                : undefined
                                                        }
                                                        invitedAproval={
                                                            onDiagramApprove
                                                                ? () => onDiagramApprove(diagram)
                                                                : undefined
                                                        }
                                                        renameAction={
                                                            onDiagramRename
                                                                ? () => onDiagramRename(diagram)
                                                                : undefined
                                                        }
                                                        shareAction={
                                                            onDiagramShare
                                                                ? () => onDiagramShare(diagram)
                                                                : undefined
                                                        }
                                                        theRouteOfAction4=""
                                                        theRouteOfAction5=""
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            {hasMoreDiagrams && (
                                <div
                                    className="add-btn2 diagram-toggle"
                                    onClick={async () => {
                                        await trackUiButtonEventAsync(
                                            UI_BUTTON_EVENTS.interview.modernShowAllDiagrams
                                        );
                                        setShowAllDiagrams((prev) => !prev);
                                    }}
                                >
                                    {showAllDiagrams ? t('words.showLess') : t('words.viewMore')}
                                </div>
                            )}
                            {!(processType === 'IMAGE' && documentMapBuildCountdown !== null && documentMapBuildCountdown > 0) && forceGenerateSidebarButton}
                        </div>

                        <hr className="ndiv" />

                        {/* INSIGHTS */}
                        <div className="lp-section" style={{ paddingBottom: 12 }}>
                            <div className="lp-label">{t('words.insights')}</div>
                            {insights.map((insight, idx) => (
                                <div key={idx} className="dia-row">
                                    <svg style={{ width: 11, height: 11, flexShrink: 0 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M2 12L6 8L9 11L13 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    {insight.name}
                                    {insight.isNew && <span className="vtag">{t('words.new')}</span>}
                                </div>
                            ))}
                            {onRunAnalysis && (
                                <div className="add-btn2" onClick={onRunAnalysis}>
                                    <svg style={{ width: 11, height: 11 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    {t('words.getNewInsight') || t('words.runNewAnalysis') || t('words.analyze')}
                                </div>
                            )}
                        </div>
                        </div>

                        <SidebarUserProfileSection />
                    </div>

                    {/* CENTRE */}
                    <div className="centre">
                        <div className="c-topbar">
                            <InterviewContextTopBar
                                processTitle={processName}
                                processType={processType}
                                extraActions={extraHeaderActions}
                            />
                        </div>

                        {children}
                    </div>

                    {/* RIGHT PANEL */}
                    <div className="right-panel">
                        <div className="rp-scroll">
                            <div className="rp-sec">
                                <div className="rp-title-row">
                                    <div className="rp-title">{t('words.progress')}</div>
                                    <span className="prog-pct">{displayProgressPercent}%</span>
                                </div>
                                <div className="prog-bar-row">
                                    <div className="prog-track">
                                        <div className="prog-fill" style={{ width: `${displayProgressPercent}%` }} />
                                    </div>
                                </div>

                                {checklistWithDetails.map((item, index) => {
                                    const isDone = item.status === 'complete';
                                    const dotClass = stepClassForStatus(item.status);
                                    const nameClass = item.status === 'upcoming' ? 'step-name muted' : 'step-name';
                                    const showLine = index < checklistWithDetails.length - 1;

                                    return (
                                        <React.Fragment key={item.id}>
                                            <div className="p-step">
                                                <div className={`step-dot ${dotClass}`}>
                                                    {isDone && <span className="step-check" aria-hidden="true">✓</span>}
                                                </div>
                                                <div className="step-info">
                                                    <div className={nameClass}>{item.label}</div>
                                                    {item.detail && <div className="step-detail">{item.detail}</div>}
                                                </div>
                                            </div>
                                            {showLine && <div className="step-line" />}
                                        </React.Fragment>
                                    );
                                })}
                            </div>

                            <div className="rp-sec rp-sec--quick">
                                <div className="rp-title">{t('words.quickActions')}</div>
                                <div className="qa-row" onClick={onStartInterview}>
                                    <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M8 2V14M2 8H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                    {t('views.processInterview.newInterview')}
                                </div>
                                <div className="qa-row sec" onClick={onOpenLibrary}>
                                    <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M2 5H8M2 8H12M2 11H10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                                    </svg>
                                    {t('views.processInterview.openProcessLibrary') || t('words.library')}
                                </div>
                                <div className="qa-row sec" onClick={onExportTranscript}>
                                    <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V9M5 6L8 3L11 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M3 11V12C3 12.6 3.4 13 4 13H12C12.6 13 13 12.6 13 12V11" stroke="currentColor" strokeWidth="1.2" />
                                    </svg>
                                    {t('views.processInterview.exportTranscript') || t('words.export')}
                                </div>
                                <div className="qa-row sec" onClick={onShare}>
                                    <svg style={{ width: 12, height: 12 }} viewBox="0 0 16 16" fill="none">
                                        <circle cx="4" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                                        <circle cx="12" cy="4" r="2" stroke="currentColor" strokeWidth="1.2" />
                                        <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.2" />
                                        <path d="M6 7L10 5M6 9L10 11" stroke="currentColor" strokeWidth="1.2" />
                                    </svg>
                                    {t('views.processInterview.share') || t('words.share')}
                                </div>
                            </div>
                        </div>

                        <div className="rp-announce-wrap">
                            <ScrollingNotification
                                className="rp-scrolling-notification"
                                messages={railNotificationMessages}
                                onDismiss={(messageId) => {
                                    console.log('Dismissed notification:', messageId);
                                }}
                                onClick={async (message) => {
                                    await trackUiButtonEventAsync(
                                        UI_BUTTON_EVENTS.interview
                                            .modernRailNotificationClick
                                    );
                                    console.log('Clicked notification:', message);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModernInterviewLayout;
